import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { useMemo } from 'react';

import { palette, tokens, useSurfaceStyles } from '../theme';
import { DayCellView } from './DayCellView';
import type { DayCell } from './models';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

export type MonthGridProps = {
    cells: DayCell[];
    today: string;
    onDayClick: (cell: DayCell) => void;
};

export const MonthGrid: React.FC<MonthGridProps> = ({ cells, today, onDayClick }) => {
    const classes = useStyles();
    const surfaces = useSurfaceStyles();

    const weeks = useMemo(() => {
        const out: DayCell[][] = [];
        for (let i = 0; i < cells.length; i += 7) {
            out.push(cells.slice(i, i + 7));
        }
        return out;
    }, [cells]);

    return (
        <div className={mergeClasses(surfaces.card, classes.root)}>
            <div className={classes.weekdayRow}>
                {WEEKDAYS.map((label) => (
                    <div key={label} className={classes.weekdayCell}>
                        {label}
                    </div>
                ))}
            </div>
            <div className={classes.gridBody}>
                {weeks.map((week, idx) => (
                    <div key={idx} className={classes.weekRow}>
                        {week.map((cell) => (
                            <DayCellView
                                key={cell.date}
                                cell={cell}
                                isToday={cell.date === today}
                                isFuture={cell.date > today}
                                onClick={onDayClick}
                            />
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 0,
        overflow: 'hidden',
    },
    weekdayRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(5, minmax(0, 2fr)) repeat(2, minmax(0, 1fr))',
        borderBottom: `1px solid ${palette.cardBorder}`,
    },
    weekdayCell: {
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
        fontSize: '11px',
        fontWeight: tokens.fontWeightBold,
        color: palette.accent,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        borderRight: `1px solid ${palette.cardBorder}`,
        '&:last-child': {
            borderRight: 'none',
        },
    },
    gridBody: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 0,
        overflowY: 'auto',
    },
    weekRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(5, minmax(0, 2fr)) repeat(2, minmax(0, 1fr))',
        flex: '1 1 0',
        minHeight: '140px',
    },
});
